import { ComponentProps } from 'react';

import { VIDEO_FORMAT, Video } from '@sorare/core/src/atoms/ui/Video';
import { useIsTabletAndAbove } from '@sorare/core/src/hooks/device/useIsTabletAndAbove';

import introLoop from './assets/16x9/intro-loop.webm';
import introToEnd from './assets/16x9/intro-to-end.webm';
import introToPromotion from './assets/16x9/intro-to-promotion.webm';
import introToRecap from './assets/16x9/intro-to-recap.webm';
import introToRelegation from './assets/16x9/intro-to-relegation.webm';
import promotionToRecap from './assets/16x9/promotion-to-recap.webm';
import promotionToRelegation from './assets/16x9/promotion-to-relegation.webm';
import recapToEnd from './assets/16x9/recap-to-end.webm';
import relegationToEnd from './assets/16x9/relegation-to-end.webm';
import relegationToRecap from './assets/16x9/relegation-to-recap.webm';
import rewardsIntro from './assets/16x9/rewards-intro.png';
import toIntro from './assets/16x9/to-intro.webm';
import introLoopMobile from './assets/9x16/intro-loop.webm';
import introToEndMobile from './assets/9x16/intro-to-end.webm';
import introToPromotionMobile from './assets/9x16/intro-to-promotion.webm';
import introToRecapMobile from './assets/9x16/intro-to-recap.webm';
import introToRelegationMobile from './assets/9x16/intro-to-relegation.webm';
import promotionToRecapMobile from './assets/9x16/promotion-to-recap.webm';
import promotionToRelegationMobile from './assets/9x16/promotion-to-relegation.webm';
import recapToEndMobile from './assets/9x16/recap-to-end.webm';
import relegationToEndMobile from './assets/9x16/relegation-to-end.webm';
import relegationToRecapMobile from './assets/9x16/relegation-to-recap.webm';
import rewardsIntroMobile from './assets/9x16/rewards-intro.png';
import toIntroMobile from './assets/9x16/to-intro.webm';

const DESKTOP_VIDEOS = {
  toIntro,
  introLoop,
  introToEnd,
  introToPromotion,
  introToRelegation,
  introToRecap,
  promotionToRelegation,
  promotionToRecap,
  promotionToEnd: undefined,
  relegationToRecap,
  relegationToEnd,
  recapToEnd,
  rewardsIntro,
};

const MOBILE_VIDEOS = {
  toIntro: toIntroMobile,
  introLoop: introLoopMobile,
  introToEnd: introToEndMobile,
  introToPromotion: introToPromotionMobile,
  introToRelegation: introToRelegationMobile,
  introToRecap: introToRecapMobile,
  promotionToRelegation: promotionToRelegationMobile,
  promotionToRecap: promotionToRecapMobile,
  promotionToEnd: undefined,
  relegationToRecap: relegationToRecapMobile,
  relegationToEnd: relegationToEndMobile,
  recapToEnd: recapToEndMobile,
  rewardsIntro: rewardsIntroMobile,
};

/* https://gitlab.com/sorare/frontend/-/blob/master/TROUBLESHOOTING.md?ref_type=heads#video-is-not-displaying-in-safari-ios */
const MOBILE_VIDEOS_MP4 = {
  toIntro:
    'https://frontend-assets.sorare.com/football/lobby/fixture-story/to-intro.mp4',
  introLoop:
    'https://frontend-assets.sorare.com/football/lobby/fixture-story/intro-loop.mp4',
  introToEnd:
    'https://frontend-assets.sorare.com/football/lobby/fixture-story/intro-out.mp4',
  introToPromotion:
    'https://frontend-assets.sorare.com/football/lobby/fixture-story/9x16/intro-to-promotion.mp4',
  introToRelegation:
    'https://frontend-assets.sorare.com/football/lobby/fixture-story/9x16/intro-to-relegation.mp4',
  introToRecap:
    'https://frontend-assets.sorare.com/football/lobby/fixture-story/9x16/intro-to-recap.mp4',
  promotionToRelegation:
    'https://frontend-assets.sorare.com/football/lobby/fixture-story/9x16/promotion-to-relegation.mp4',
  promotionToRecap:
    'https://frontend-assets.sorare.com/football/lobby/fixture-story/9x16/promotion-to-recap.mp4',
  promotionToEnd:
    'https://frontend-assets.sorare.com/football/lobby/fixture-story/9x16/promotion-to-end.mp4',
  relegationToRecap:
    'https://frontend-assets.sorare.com/football/lobby/fixture-story/9x16/relegation-to-recap.mp4',
  relegationToEnd:
    'https://frontend-assets.sorare.com/football/lobby/fixture-story/9x16/relegation-to-end.mp4',
  recapToEnd:
    'https://frontend-assets.sorare.com/football/lobby/fixture-story/9x16/recap-to-end.mp4',
  rewardsIntro: rewardsIntroMobile,
};

const desktopVideosSources = Object.entries(DESKTOP_VIDEOS).reduce(
  (acc, [key, src]) => {
    acc[key as keyof typeof DESKTOP_VIDEOS] = [
      {
        src,
        type: VIDEO_FORMAT.WEBM,
      },
    ];
    return acc;
  },
  {} as {
    [key in keyof typeof DESKTOP_VIDEOS]: ComponentProps<
      typeof Video
    >['sources'];
  }
);

const mobileVideoSources = Object.entries(MOBILE_VIDEOS).reduce(
  (acc, [key, src]) => {
    acc[key as keyof typeof MOBILE_VIDEOS] = [
      {
        src,
        type: VIDEO_FORMAT.WEBM,
      },
      {
        src: MOBILE_VIDEOS_MP4[key as keyof typeof MOBILE_VIDEOS],
        type: VIDEO_FORMAT.MP4,
      },
    ];
    return acc;
  },
  {} as {
    [key in keyof typeof MOBILE_VIDEOS]: ComponentProps<
      typeof Video
    >['sources'];
  }
);

export const useResultsStoryAssets = () => {
  const isTabletAndAbove = useIsTabletAndAbove();
  return isTabletAndAbove ? desktopVideosSources : mobileVideoSources;
};
